"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Cropper from "react-cropper";

import { cn } from "@/lib/utils";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui";

import LoadingDots from "../icons/loading-dots";

import "cropperjs/dist/cropper.css";

// ---------------------------------------------------------------------------
// Crop modal — same UX as inbox-web ImageCropModal
// ---------------------------------------------------------------------------

const MAX_CROP_DIMENSION = 1024;

interface CropModalProps {
  open: boolean;
  onToggle: (open: boolean) => void;
  onConfirm: (dataUrl: string) => Promise<void>;
  imageFile: string | null;
}

function CropModal({ open, onToggle, onConfirm, imageFile }: CropModalProps) {
  const [cropper, setCropper] = useState<Cropper | null>(null);

  useEffect(() => {
    if (!open) {
      setCropper(null);
    }
  }, [open]);

  const handleCrop = useCallback(async () => {
    if (!cropper) return;
    try {
      // Cap the output size: an uncapped PNG re-encode of a large photo can
      // exceed the 4.5MB serverless request body limit on upload.
      const dataUrl = cropper
        .getCroppedCanvas({
          maxWidth: MAX_CROP_DIMENSION,
          maxHeight: MAX_CROP_DIMENSION,
        })
        .toDataURL();
      await onConfirm(dataUrl);
    } catch {
      onToggle(false);
    }
  }, [cropper, onConfirm, onToggle]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleCrop();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleCrop]);

  return (
    <Dialog open={open} onOpenChange={onToggle}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Image</DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {imageFile && open && (
            <Cropper
              style={{ marginTop: 8 }}
              zoomTo={0}
              initialAspectRatio={1}
              src={imageFile}
              minCropBoxHeight={4}
              minCropBoxWidth={4}
              autoCropArea={1}
              checkOrientation={false}
              onInitialized={(instance) => {
                setCropper(instance);
              }}
              responsive
              guides
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onToggle(false)}>
            Cancel
            <kbd className="ml-1.5 rounded border border-border px-1 font-mono text-[10px] leading-4 text-muted-foreground">
              [ESC]
            </kbd>
          </Button>
          <Button onClick={handleCrop}>
            Upload
            <kbd className="ml-1.5 rounded border border-primary-foreground/20 px-1 font-mono text-[10px] leading-4 text-primary-foreground/60">
              [↵]
            </kbd>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Uploader
// ---------------------------------------------------------------------------

export default function Uploader({
  defaultValue,
  name,
  onChange,
  loading,
  withCropper,
  disabled,
  onFileSelected,
}: {
  defaultValue: string | null;
  name: "image" | "logo" | "coverImage";
  onChange?: (dataUrl: string, file?: File) => void | Promise<void>;
  loading?: boolean;
  withCropper?: boolean;
  disabled?: boolean;
  onFileSelected?: () => void;
}) {
  const aspectRatio =
    name === "image"
      ? "aspect-video"
      : name === "coverImage"
        ? "aspect-[4/1]"
        : "aspect-square";

  const inputRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState({ [name]: defaultValue });
  const [dragActive, setDragActive] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<string | null>(null);

  useEffect(() => {
    if (defaultValue) {
      setData((prev) => ({ ...prev, [name]: defaultValue }));
    }
  }, [defaultValue, name]);

  const handleUpload = (file: File | null) => {
    if (!file) return;

    if (file.size / 1024 / 1024 > 4) {
      toast.error("File size too big (max 4MB)");
      return;
    }

    if (
      !file.type.includes("png") &&
      !file.type.includes("jpg") &&
      !file.type.includes("jpeg")
    ) {
      toast.error("Invalid file type (must be .png, .jpg, or .jpeg)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      onFileSelected?.();

      if (withCropper) {
        setPendingFile(result);
        setCropModalOpen(true);
      } else {
        // Show the picked image right away; the saved URL replaces it via
        // defaultValue after the form submits and the route refreshes.
        setData((prev) => ({ ...prev, [name]: result }));
        onChange?.(result, file);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async (dataUrl: string) => {
    setCropModalOpen(false);
    setPendingFile(null);
    setData((prev) => ({ ...prev, [name]: dataUrl }));
    await onChange?.(dataUrl);
  };

  const handleCropCancel = (open: boolean) => {
    if (!open) {
      setCropModalOpen(false);
      setPendingFile(null);
      // Reset input so the same file can be re-selected
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  return (
    <div>
      <label
        onClick={(e) => {
          if (disabled) return;
          e.preventDefault();
          inputRef.current?.click();
        }}
        className={cn(
          "group relative flex cursor-pointer flex-col items-center justify-center rounded-lg border border-gray-300 bg-card shadow-sm transition-all hover:bg-gray-50 dark:border-grunge80",
          aspectRatio,
          {
            "max-w-screen-md": aspectRatio === "aspect-video",
            "max-w-xs": aspectRatio === "aspect-square",
            "cursor-not-allowed opacity-50": disabled,
          },
        )}
      >
        <div
          className="absolute z-[5] h-full w-full rounded-xl"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(true);
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
            const file = e.dataTransfer.files?.[0] ?? null;
            if (inputRef.current && e.dataTransfer.files) {
              inputRef.current.files = e.dataTransfer.files;
            }
            handleUpload(file);
          }}
        />
        <div
          className={`${
            dragActive ? "border-2 border-black" : ""
          } absolute z-[3] flex h-full w-full flex-col items-center justify-center rounded-md px-10 transition-all ${
            data[name]
              ? "bg-white/80 opacity-0 hover:opacity-100 hover:backdrop-blur-md dark:bg-black/80"
              : "bg-card opacity-100 hover:bg-gray-50 dark:bg-black"
          }`}
        >
          <svg
            className={`${
              dragActive ? "scale-110" : "scale-100"
            } h-7 w-7 text-gray-500 transition-all duration-75 group-hover:scale-110 group-active:scale-95 dark:text-white`}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path>
            <path d="M12 12v9"></path>
            <path d="m16 16-4-4-4 4"></path>
          </svg>
          <p className="mt-2 text-center text-sm text-gray-500 dark:text-white">
            Drag and drop or click to upload.
          </p>
          <p className="mt-2 text-center text-sm text-gray-500 dark:text-white">
            Max file size: 4MB
          </p>
          <span className="sr-only">Photo upload</span>
        </div>
        {data[name] && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={data[name] as string}
            alt="Preview"
            className="h-full w-full rounded-md object-cover"
          />
        )}

        {loading && (
          <div className="absolute z-[4] flex h-full w-full flex-col items-center justify-center rounded-md bg-card/80 hover:backdrop-blur-md dark:bg-black/80">
            <LoadingDots />
          </div>
        )}
      </label>
      <div className="mt-1 flex rounded-md shadow-sm">
        <input
          id={`${name}-upload`}
          ref={inputRef}
          name={name}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const file = e.currentTarget.files?.[0] ?? null;
            handleUpload(file);
          }}
        />
      </div>

      {withCropper && (
        <CropModal
          open={cropModalOpen}
          onToggle={handleCropCancel}
          onConfirm={handleCropConfirm}
          imageFile={pendingFile}
        />
      )}
    </div>
  );
}
